import React, { useEffect, useRef, useState } from "react";
import { Form, Button } from "react-bootstrap";
import "../../_css/Login.css";
import { Eye, EyeOff } from "lucide-react";
import OTPInput from "./OTPInput";
import { loginUser, setPassword as setPasswordRequest, verifyAccount } from "../../requests";
import {
  getFormattedEmail,
  notifyError,
  notifySuccess,
  saveAuthToken,
} from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { CustomButton, CustomButtonLight } from "../UI/CustomButtons";

// Note: No changePassword API imported, as per your request

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  // Ensure otpPage flag is properly set on forgot password flow
  const [otpPage, setOtpPage] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [rememberMe, setRememberMe] = useState(false);
  const [timer, setTimer] = useState(0);
  const [forgotPassword, setForgotPassword] = useState(false);

  const [resetPassword, setResetPassword] = useState(false);

  const Navigate = useNavigate();
  const timerRef = useRef(null);

  // useEffect(() => {
  //   if (forgotPassword) {
  //     setTimer(60);
  //     // Make sure to show OTP input when forgotPassword is true and resetPassword false
  //     if (!resetPassword) setOtpPage(true);
  //   } else {
  //     setTimer(0);
  //     setOtpPage(false);
  //   }
  // }, [forgotPassword, resetPassword]);

  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setTimeout(() => setTimer(timer - 1), 1000);
    } else {
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [timer]);

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address.";
    }
    if (!forgotPassword && !resetPassword) {
      if (!password) {
        newErrors.password = "Password is required.";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateResetPassword = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = "Password is required.";
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAccountLogin = async (newOtp = false) => {
    try {
      const response = await loginUser({
        email: email,
        password: password,
        newOtp: newOtp,
      });
      if (response.status === 201) {
        setForgotPassword(true);
        setOtpPage(true);
        setResetPassword(false); // Ensure resetPassword is false here
      } else if (response.status === 200) {
        notifySuccess(response.data.message);
        saveAuthToken(response.data.token);
        Navigate("/");
      }
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 401) {
        notifyError(error.response.data.message);
      } else if (error.response) {
        notifyError(error.response.data.message);
      } else {
        notifyError("Something went wrong");
      }
    }
  };

  const handleAccountVerification = async () => {
    try {
      const response = await verifyAccount({
        email: email,
        otp: otp.join(""),
      });
      if (response.status === 200) {
        notifySuccess(response.data.message);
        setOtpPage(false);
        setResetPassword(true);
      }

      // putting outside for functionality checking
      // setOtpPage(false);
      // setResetPassword(true);
    } catch (error) {
      console.log(error);
      if (error.response && error.response.status === 401) {
        notifyError(error.response.data.message);
      } else if (error.response) {
        notifyError(error.response.data.message);
      } else {
        notifyError("Something went wrong");
      }
    }
  };

  // TODO: Implement actual password reset API call here
  const handlePasswordReset = async () => {
    if (!validateResetPassword()) return;

    try {
      try {
        const response = await setPasswordRequest({
          password: newPassword,
          email: email,
          otp: otp.join(""),
        });
        if (response.status === 200) {
          notifySuccess(response.data.message);
          setForgotPassword(false);
          setResetPassword(false);
          setOtpPage(false);
          setNewPassword("");
          setConfirmPassword("");
          Navigate("/login");
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          notifyError(error.response.data.message);
        } else if (error.response) {
          notifyError(error.response.data.message);
        } else {
          notifyError("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        notifyError(error.response.data.message);
      } else {
        notifyError("Something went wrong");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (forgotPassword) {
      if (otpPage) {
        if (otp) {
          await handleAccountVerification();
        }
      } else if (resetPassword) {
        await handlePasswordReset();
      }
    } else {
      if (validate()) {
        await handleAccountLogin();
      }
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center gap-4">
      <div
        className={`${
          forgotPassword ? "forgot-password-container" : "login-container"
        }`}
      >
        <img
          className="mb-3"
          style={{ width: "10%" }}
          src={`${process.env.REACT_APP_PROJECT_URL}/img/askSalah.png`}
          alt="logo"
        />
        <h3 className="fw-600 text-color-1 ">
          {forgotPassword
            ? resetPassword
              ? "Set New Password"
              : "Forgot Password?"
            : "Sign In to your Account"}
        </h3>

        {forgotPassword && !resetPassword ? (
          <span style={{ marginTop: "-1rem" }} className="mb-4">
            Code sent to {getFormattedEmail(email)}
          </span>
        ) : resetPassword ? (
          <span className="text-color-2" style={{ marginTop: "-1rem" }}>
            Must be at least 8 characters long, contain a lowercase letter, an
            uppercase letter, and at least one special character.
          </span>
        ) : (
          <span className="text-color-2">
            Welcome back! please enter your details
          </span>
        )}

        <Form className="mt-3" onSubmit={handleSubmit}>
          {forgotPassword ? (
            otpPage ? (
              <OTPInput otp={otp} setOtp={setOtp} />
            ) : resetPassword ? (
              <>
                <Form.Group className="mb-3" controlId="formNewPassword">
                  <Form.Label className="text-color-3">New Password</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      isInvalid={!!errors.password}
                    />
                    <Button
                      variant=""
                      onClick={() => setShowPassword(!showPassword)}
                      className="show-password-btn"
                      type="button"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.password}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formConfirmPassword">
                  <Form.Label className="text-color-3">
                    Confirm Password
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      isInvalid={!!errors.confirmPassword}
                    />
                    <Button
                      variant=""
                      onClick={() => setShowPassword(!showPassword)}
                      className="show-password-btn"
                      type="button"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmPassword}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </>
            ) : null
          ) : (
            <>
              <Form.Group className="mb-3" controlId="formEmail">
                <Form.Label className="text-color-3">Work email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="john.dowry@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isInvalid={!!errors.email}
                  style={{ backgroundColor: "white" }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2" controlId="formPassword">
                <Form.Label className="text-color-3">Password</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    isInvalid={!!errors.password}
                  />
                  <Button
                    variant=""
                    onClick={() => setShowPassword(!showPassword)}
                    className="show-password-btn"
                    type="button"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </>
          )}

          <Form.Text className="d-flex justify-content-between mt-3 mb-3">
            {!forgotPassword && (
              <div className="d-flex align-items-center gap-1">
                <input
                  value={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  type="checkbox"
                />
                <label className="text-muted">Remember me</label>
              </div>
            )}
            {!forgotPassword ? (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAccountLogin(true);
                }}
                className="forgot-password btn text-color-4 fw-700"
                href="#forgot-password"
              >
                Forgot Password?
              </button>
            ) : (
              !resetPassword && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAccountLogin(true);
                  }}
                  className="forgot-password btn text-color-4 fw-700"
                  href="#forgot-password"
                  disabled={timer > 0}
                  style={{
                    cursor: timer > 0 ? "not-allowed" : "pointer",
                    opacity: timer > 0 ? 0.6 : 1,
                  }}
                >
                  {timer > 0
                    ? `Send Again? Please wait (${timer}s)`
                    : "Send Again?"}
                </button>
              )
            )}
          </Form.Text>

          <div className="d-flex mt-3 justify-content-between">
            {forgotPassword && !resetPassword && (
              <CustomButtonLight
                text="Back"
                onClick={() => {
                  setForgotPassword(false);
                  setOtpPage(false);
                  setResetPassword(false);
                }}
                width="49%"
              />
            )}
            <CustomButton
              text={
                forgotPassword
                  ? resetPassword
                    ? "Reset Password"
                    : "Verify OTP"
                  : "Sign In"
              }
              onClick={handleSubmit}
              width={forgotPassword && !resetPassword ? "49%" : "100%"}
            />
          </div>
        </Form>
      </div>
      {!forgotPassword && (
        <img
          src={`${process.env.REACT_APP_PROJECT_URL}/img/dr-salah.png`}
          alt="askSalahLoginPage"
          style={{ width: "50%" }}
        />
      )}
    </div>
  );
};

export default Login;
